import { DatePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OnuViewService } from '@modules/clients/services/olt/onu-view.service';
import * as Highcharts from 'highcharts';
import 'highcharts/modules/xrange';
import { HighchartsChartModule } from 'highcharts-angular';
import { SelectButtonModule } from 'primeng/selectbutton';

interface IGraphSeries {
  name: string;
  points: Array<[number, number | null]>;
  /** 'p95' en las líneas de percentil 95 de Mes y Año */
  role?: string;
}

interface IStatusSegment {
  from: number;
  to: number;
  status: string;
}

type GraphKind = 'traffic' | 'signal';

const STATUS_COLORS: Partial<Record<string, string>> = {
  Online: '#1fb325',
  'Power fail': '#0064C8',
  LOS: '#dc2626',
  Offline: '#4c4b4b',
  'Log in': '#eab308',
  Flapping: '#9333ea',
};

/**
 * Gráficos de la ONU como en SmartOLT: tráfico (bps), señal (dBm) y la banda de estado de 24 h.
 */
@Component({
  selector: 'app-onu-graphs',
  standalone: true,
  imports: [DatePipe, FormsModule, HighchartsChartModule, SelectButtonModule],
  templateUrl: './onu-graphs.component.html',
})
export class OnuGraphsComponent implements OnInit {
  @Input({ required: true }) onuId!: number;

  public readonly Highcharts: typeof Highcharts = Highcharts;
  public readonly statusColors = STATUS_COLORS;
  public readonly kinds: GraphKind[] = ['traffic', 'signal'];
  public readonly periods: Record<GraphKind, { label: string; value: string }[]> = {
    traffic: [
      { label: 'Hora', value: 'hourly' },
      { label: 'Día', value: 'daily' },
      { label: 'Semana', value: 'weekly' },
      { label: 'Mes', value: 'monthly' },
      { label: 'Año', value: 'yearly' },
    ],
    signal: [
      { label: 'Día', value: 'daily' },
      { label: 'Semana', value: 'weekly' },
      { label: 'Mes', value: 'monthly' },
      { label: 'Año', value: 'yearly' },
    ],
  };
  public period: Record<GraphKind, string> = { traffic: 'daily', signal: 'daily' };
  public charts: Record<GraphKind, Highcharts.Options | null> = { traffic: null, signal: null };
  public empty: Record<GraphKind, boolean> = { traffic: false, signal: false };
  /** en Mes y Año, primer día con datos cuando el periodo empieza antes (ONU nueva o sin lecturas) */
  public dataSince: Record<GraphKind, number | null> = { traffic: null, signal: null };
  /** actual y máximo de cada serie, como la leyenda de SmartOLT */
  public stats: Record<GraphKind, { name: string; color: string; current: string; max: string }[]> =
    {
      traffic: [],
      signal: [],
    };
  public usage: { period: string; download: string; upload: string; total: string }[] | null = null;
  public usageOpen = false;
  public statusChart: Highcharts.Options | null = null;
  public statusLoaded = false;
  /** estados presentes en las últimas 24 h, para la leyenda */
  public statusPresent: string[] = [];

  constructor(private readonly onuViewService: OnuViewService) {}

  ngOnInit(): void {
    this.load('traffic');
    this.load('signal');
    this.loadStatusHistory();
  }

  /**
   * load: series del gráfico para el periodo elegido
   */
  public async load(kind: GraphKind) {
    try {
      const response = await this.onuViewService.read<{ series: IGraphSeries[] }>(
        this.onuId,
        kind,
        {
          period: this.period[kind],
        },
      );
      // como la gráfica de la ONU en SmartOLT: solo los promedios, sin las líneas P95
      // (casi vacías en el año, quedaban como puntos sueltos)
      const averages = (response.data?.series ?? []).filter((s) => s.role !== 'p95');
      const { series, since } = this.trimLeadingGap(
        averages,
        ['monthly', 'yearly'].includes(this.period[kind]),
      );
      this.dataSince[kind] = since;
      this.empty[kind] = !series.some((s) => s.points?.some((p) => p[1] !== null));
      this.charts[kind] = this.empty[kind] ? null : this.chartOptions(kind, series);
      this.stats[kind] = this.empty[kind] ? [] : series.map((s) => this.seriesStats(kind, s));
    } catch (error) {
      this.empty[kind] = true;
      this.charts[kind] = null;
      this.dataSince[kind] = null;
    }
  }

  /**
   * trimLeadingGap: Mes y Año traen un punto por día de todo el periodo; si la ONU solo tiene datos
   * de los últimos días, esos puntos quedan apretados al final del eje y el gráfico parece vacío.
   * Se quitan los días sin datos del principio (en todas las series) y se devuelve el primer día con datos.
   */
  public trimLeadingGap(
    series: IGraphSeries[],
    enabled: boolean,
  ): { series: IGraphSeries[]; since: number | null } {
    if (!enabled || !series.length) return { series, since: null };
    const firstIndexes = series
      .map((s) => s.points.findIndex((p) => p[1] !== null))
      .filter((index) => index >= 0);
    if (!firstIndexes.length) return { series, since: null };
    const first = Math.min(...firstIndexes);
    if (first === 0) return { series, since: null };
    return {
      series: series.map((s) => ({ ...s, points: s.points.slice(first) })),
      since: series.find((s) => s.points[first])?.points[first][0] ?? null,
    };
  }

  private chartOptions(kind: GraphKind, series: IGraphSeries[]): Highcharts.Options {
    const isTraffic = kind === 'traffic';
    // promedios diarios (Mes y Año): pocos puntos, se marcan para que se vean
    const daily = ['monthly', 'yearly'].includes(this.period[kind]);
    const fewPoints = Math.max(...series.map((s) => s.points.length)) <= 31;
    return {
      accessibility: { enabled: false },
      // igual que SmartOLT: hora local del navegador; los promedios diarios son días UTC
      time: { timezone: daily ? 'UTC' : Intl.DateTimeFormat().resolvedOptions().timeZone },
      chart: { type: isTraffic ? 'area' : 'line', height: 220, zooming: { type: 'x' } },
      title: { text: isTraffic ? 'Tráfico' : 'Señal', style: { fontSize: '13px' } },
      credits: { enabled: false },
      xAxis: { type: 'datetime', dateTimeLabelFormats: { hour: '%H:%M', minute: '%H:%M' } },
      yAxis: isTraffic
        ? {
            title: { text: 'bits por segundo' },
            labels: { formatter: (ctx) => this.formatBps(Number(ctx.value)) },
          }
        : { title: { text: 'dBm' } },
      tooltip: {
        shared: true,
        xDateFormat: daily ? '%d/%m/%Y' : '%d/%m %H:%M',
        valueSuffix: isTraffic ? ' bps' : ' dBm',
      },
      plotOptions: {
        series: { marker: { enabled: daily && fewPoints, radius: 3 } },
        area: { fillOpacity: 0.25 },
      },
      series: series.map((s) => ({
        type: isTraffic ? 'area' : 'line',
        name: s.name,
        color: this.seriesColor(s.name),
        data: s.points,
      })),
    };
  }

  private seriesColor(name: string): string {
    return /upload/i.test(name) ? '#f97316' : /1310|olt/i.test(name) ? '#7c3aed' : '#2563eb';
  }

  private seriesStats(kind: GraphKind, series: IGraphSeries) {
    const values = series.points.map((p) => p[1]).filter((v): v is number => v !== null);
    const current = values.length ? values[values.length - 1] : null;
    const max = values.length ? Math.max(...values) : null;
    const format = (v: number | null) =>
      v === null
        ? 'N/A'
        : kind === 'traffic'
        ? `${(v / 1e6).toFixed(2)} Mbps`
        : `${v.toFixed(2)} dBm`;
    return {
      name: series.name,
      color: this.seriesColor(series.name),
      current: format(current),
      max: format(max),
    };
  }

  /**
   * toggleUsage: consumo de hoy, ayer, semana y mes (botón "Traffic usage" de SmartOLT)
   */
  public async toggleUsage() {
    this.usageOpen = !this.usageOpen;
    if (!this.usageOpen || this.usage) return;
    try {
      const response = await this.onuViewService.read<
        { period: string; download: string; upload: string; total: string }[]
      >(this.onuId, 'traffic_usage');
      this.usage = response.data;
    } catch (error) {
      this.usage = [];
    }
  }

  private formatBps(value: number): string {
    if (value >= 1e9) return `${(value / 1e9).toFixed(1)}G`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(0)}k`;
    return String(value);
  }

  /**
   * loadStatusHistory: estados de las últimas 24 horas en un gráfico de rangos,
   * con tooltip del estado, el intervalo y su duración
   */
  public async loadStatusHistory() {
    try {
      const response = await this.onuViewService.read<{ segments?: IStatusSegment[] }>(
        this.onuId,
        'status_history',
      );
      const segments = response.data?.segments ?? [];
      this.statusPresent = [...new Set(segments.map((s) => s.status))];
      this.statusChart = segments.length ? this.statusChartOptions(segments) : null;
    } catch (error) {
      this.statusChart = null;
    } finally {
      this.statusLoaded = true;
    }
  }

  private statusChartOptions(segments: IStatusSegment[]): Highcharts.Options {
    const formatDuration = (ms: number) => {
      const minutes = Math.round(ms / 60000);
      const hours = Math.floor(minutes / 60);
      return hours ? `${hours}h ${String(minutes % 60).padStart(2, '0')}m` : `${minutes}m`;
    };
    return {
      accessibility: { enabled: false },
      time: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      chart: { type: 'xrange', height: 110, spacingTop: 4, zooming: { type: 'x' } },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: { type: 'datetime', dateTimeLabelFormats: { hour: '%H:%M', minute: '%H:%M' } },
      yAxis: {
        title: { text: '' },
        categories: [''],
        reversed: true,
        labels: { enabled: false },
        gridLineWidth: 0,
      },
      tooltip: {
        useHTML: true,
        formatter: function () {
          const point = this as unknown as {
            x: number;
            x2: number;
            color: string;
            name: string;
            series: { chart: { time: Highcharts.Time } };
          };
          // con la zona horaria del gráfico (Highcharts.dateFormat usaría UTC)
          const fmt = (t: number) => point.series.chart.time.dateFormat('%d/%m %H:%M', t);
          return (
            `<span style="color:${point.color}">●</span> <b>${point.name}</b><br/>` +
            `${fmt(point.x)} → ${fmt(point.x2)}<br/>Duración: ${formatDuration(point.x2 - point.x)}`
          );
        },
      },
      plotOptions: {
        xrange: { borderRadius: 2, pointPadding: 0, groupPadding: 0, borderWidth: 0 },
      },
      series: [
        {
          type: 'xrange',
          name: 'Estado',
          pointWidth: 26,
          data: segments.map((s) => ({
            x: s.from,
            x2: s.to,
            y: 0,
            name: s.status,
            color: STATUS_COLORS[s.status] ?? '#9ca3af',
          })),
        },
      ],
    };
  }
}
